import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../../components/BaseUrl";
import {
  convertUTCToLocalDateTime,
  formatDurationMinutes,
  hoursDecimalToMinutes,
} from "../../../components/dateTimeUtils";
import {useTranslation} from "react-i18next"

const weekDays = [
  { letter: "S", code: "Sun" },
  { letter: "M", code: "Mon" },
  { letter: "T", code: "Tue" },
  { letter: "W", code: "Wed" },
  { letter: "T", code: "Thu" },
  { letter: "F", code: "Fri" },
  { letter: "S", code: "Sat" },
];
const defaultZoom = 10;
const fallbackZoom = 2;
const defaultMarkerUrl = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png";

const getMarkerIcon = () => {
  if (!window.google) {
    return { url: defaultMarkerUrl };
  }

  return {
    url: defaultMarkerUrl,
    scaledSize: new window.google.maps.Size(25, 41),
    anchor: new window.google.maps.Point(12, 41),
  };
};

const AttendanceSettingsView = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const {t}=useTranslation()
  useEffect(() => {
    const fetchAttendanceSettings = async () => {
      try {
        const res = await fetch(`${BASE_URL}/attendance/my-schedule`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        console.log("📄 Attendance Settings fetched:", data);

        if (!res.ok) throw new Error(data.message || "Failed to load settings");

        setSettings(data.data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceSettings();
  }, [token]);
const formatUtcShiftTimeForDisplay = (timeString) => {
  if (!timeString) return "";
  const formatted = convertUTCToLocalDateTime(timeString, {
    formatOptions: {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    },
  });
  return formatted === "—" ? "" : formatted;
};

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );

  if (!settings)
    return (
      <Box textAlign="center" mt={5}>
       {t("attendance.noDataFound")}
      </Box>
    );
// if (!settings?.location) {
//   return null; // or fallback UI
// }
//   const { schedule, location } = settings;
// const safeDays = Array.isArray(schedule?.days) ? schedule.days : [];
const schedule = settings?.schedule || {};
const location = settings?.location || {};

const safeDays = Array.isArray(schedule.days) ? schedule.days : [];

const safeLat = typeof location.lat === "number" ? location.lat : 0;
const safeLng = typeof location.lng === "number" ? location.lng : 0;
const safeRadius = typeof location.radiusMeters === "number" ? location.radiusMeters : 0;
const hasValidLocation = Boolean(safeLat && safeLng);
  return (
    <Box p={3}>
      {/* Days UI */}
      <Box display="flex" justifyContent="center" gap={2} mb={3}>
      {weekDays.map((day) => {
          const active = safeDays.includes(day.code);

          return (
            <Box
              key={day.code}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: active ? "#1E73BE" : "#D6D6D6",
                color: active ? "white" : "black",
                fontWeight: 600,
                cursor: "default",
              }}
            >
              {day.letter}
            </Box>
          );
        })}
      </Box>

      {/* Map */}
      <Box
        sx={{
          height: 350,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #ddd",
          mb: 4,
        }}
      >
        <GoogleMap
          center={{ lat: safeLat, lng: safeLng }}
          zoom={hasValidLocation ? defaultZoom : fallbackZoom}
          mapContainerStyle={{ height: "100%", width: "100%" }}
          options={{
            scrollwheel: false,
            draggable: false,
            disableDoubleClickZoom: true,
            zoomControl: false,
            disableDefaultUI: true,
            keyboardShortcuts: false,
            gestureHandling: "none",
          }}
        >
          {hasValidLocation && (
            <>
              <Marker
                position={{ lat: safeLat, lng: safeLng }}
                icon={getMarkerIcon()}
              />
              <Circle
                center={{ lat: safeLat, lng: safeLng }}
                radius={safeRadius}
                options={{
                  strokeColor: "#1E73BE",
                  fillColor: "#1E73BE",
                  strokeOpacity: 1,
                  fillOpacity: 0.15,
                }}
              />
            </>
          )}
        </GoogleMap>
      </Box>

      {/* Location Name */}
      <Box mb={2}>
        <Box  sx={{
        fontSize: "17px",
        fontWeight: 500,
        fontFamily: "'Poppins', sans-serif",
        color: "#6B778C",
        display: "inline-block",
        marginBottom: "4px",
      }}>
          {t("attendance.locationName")}
:
        </Box>
        <Box  sx={{
        fontSize: "14px",
        fontWeight: 400,
       color: "#6B778C",
      }}>{location.locationName}</Box>
      </Box>

      {/* Schedule Details */}
      {/* <Box mt={3}>
       <DetailRow label={t("attendance.startTime")}  value={formatTime(schedule.start)} />
<DetailRow label={t("attendance.endTime")}  value={formatTime(schedule.end)} />
        <DetailRow label={t("attendance.dailyHours")} value={schedule?.dailyHours + " Hours"} />
        <DetailRow label={t("attendance.breakUnpaid")} value={schedule?.breakPolicy?.unpaid + " Minutes"} />
        <DetailRow label={t("attendance.graceMinutes")} value={schedule?.graceMinutes + " Minutes"} />
      </Box> */}

      <Box mt={3}>
  <DetailRow
    label={t("attendance.startTime")}
    value={formatUtcShiftTimeForDisplay(schedule?.start) || "--"}
  />

  <DetailRow
    label={t("attendance.endTime")}
    value={formatUtcShiftTimeForDisplay(schedule?.end) || "--"}
  />

  <DetailRow
    label={t("attendance.dailyHours")}
    value={
      schedule?.dailyHours != null
        ? formatDurationMinutes(hoursDecimalToMinutes(schedule.dailyHours)) ?? "--"
        : "--"
    }
  />

  <DetailRow
    label={t("attendance.breakUnpaid")}
    value={
      schedule?.breakPolicy?.unpaid != null
        ? `${schedule.breakPolicy.unpaid} Minutes`
        : "--"
    }
  />

  <DetailRow
    label={t("attendance.graceMinutes")}
    value={
      schedule?.graceMinutes != null
        ? `${schedule.graceMinutes} Minutes`
        : "--"
    }
  />
</Box>
    </Box>
  );
};

// Component for clean detail rows
const DetailRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    borderBottom="1px solid #e0e0e0"
    py={1.5}
  >
    <Box
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: "'Poppins', sans-serif",
        color: "#6B778C",
        display: "inline-block",
        marginBottom: "4px",
      }}
    >
      {label}
    </Box>

    <Box
      sx={{
        fontSize: "14px",
        fontWeight: 400,
       color: "#6B778C",
      }}
    >
      {value}
    </Box>
  </Box>
);


export default AttendanceSettingsView;
